import "@/styles/dividend-payout-print.css";
/**
 * Дивиденды — расчётный лист + журнал выплат.
 *
 * Главная страница: РАСЧЁТНЫЙ ЛИСТ дивидендов — параметры (период + доли)
 * + итоги + полные детали (приходы по объектам с датами, расходы по
 * объектам, накладные, замороженные WIP, маржа по объектам, распределение
 * между партнёрами). По кнопке «Зафиксировать» — короткое подтверждение
 * (только дата выплаты + заметка), всё остальное берётся из листа. После
 * фиксации появляется в Истории.
 *
 * Снимок выплаты неизменяем. Первичка, попавшая в выплату, защищена от
 * удаления (server 409 → confirm → ?force=true).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { Avatar } from "@/components/prototype/Avatar";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchDividendPayouts, fetchDividendPayout, previewDividendPayout,
  createDividendPayout, createDividendTopUp, patchDividendPayout, deleteDividendPayout,
  fetchPayrollConfigs, formatRub,
  fetchFundAllocations, createFundAllocation, deleteFundAllocation,
  setObjectDistributionStatus, acceptObjectDistribution, cancelObjectDistributionAcceptance,
  type DividendPayoutDetail, type PayoutSnapshot,
  type OwnerShare, type PayrollConfigRow, type FundAllocationRow,
  type PayoutObjectDistributionStatus,
} from "@/api/finance";

const rub = (v: number) => formatRub(v, { mln: false });

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const OVERHEAD_LABELS: Record<string, string> = {
  marketing: "Маркетинг",
  sales: "Отдел продаж (ОП)",
  other: "Прочие",
  unassigned_production: "Производство без объекта",
  taxes: "Налоги",
};

/** Чипы периода. */
function PeriodChips({ from, to, onChange }: {
  from: string; to: string; onChange: (f: string, t: string) => void;
}) {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const t = iso(today);
  const ago = (n: number) => iso(new Date(today.getTime() - n * 86400000));
  const yStart = iso(new Date(Date.UTC(today.getUTCFullYear(), 0, 1)));
  const mStart = iso(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)));
  const presets: [string, string, string][] = [
    ["Всё время", "", ""],
    ["Этот год", yStart, t],
    ["Этот месяц", mStart, t],
    ["Квартал", ago(89), t],
    ["365 дней", ago(364), t],
  ];
  return (
    <>
      {presets.map(([label, f, tt]) => (
        <button key={label} type="button"
                className={`chip ${from === f && to === tt ? "active" : ""}`}
                onClick={() => onChange(f, tt)}>
          {label}
        </button>
      ))}
    </>
  );
}

export function FinanceDividendPayoutsPage() {
  const { user } = useAuth();
  const isOwner = !!user?.is_superuser;
  // Доли владельцев меняет только главный администратор (Геннадий).
  // Остальные совладельцы видят значения, но редактировать не могут.
  const canEditShares = !!user?.is_superuser && user?.role === "admin";
  const [openId, setOpenId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Параметры расчётного листа (период + собственники)
  const [periodFrom, setPeriodFrom] = useState<string>("");
  const [periodTo, setPeriodTo] = useState<string>("");
  const [owners, setOwners] = useState<OwnerShare[]>([]);
  // Отчисления в фонды: fund_id → сумма (можно переопределить от планового %).
  // Инициализируется из snap.fund_allocations_planned при загрузке.
  const [fundOverrides, setFundOverrides] = useState<Map<number, number>>(new Map());
  // Фильтр объектов в таблице «Объекты и маржа»: все / завершённые / в работе
  const [objStatusFilter, setObjStatusFilter] = useState<"all" | "done" | "wip">("all");
  // Фильтр по статусу распределения: все / учтён / частично / не учтён
  const [distFilter, setDistFilter] = useState<"all" | "full" | "partial" | "none">("all");

  const listQ = useQuery({
    queryKey: ["dividend-payouts"],
    queryFn: fetchDividendPayouts,
    enabled: isOwner,
    staleTime: 30_000,
  });
  const cfgQ = useQuery({
    queryKey: ["finance-payroll-configs"],
    queryFn: fetchPayrollConfigs,
    enabled: isOwner,
    staleTime: 60_000,
  });
  const ownerCandidates = useMemo(
    () => (cfgQ.data?.items ?? []).filter(u => u.is_superuser || u.role === "owner"),
    [cfgQ.data]);

  const previewQ = useQuery({
    queryKey: ["dividend-preview-sheet", periodFrom, periodTo],
    queryFn: () => previewDividendPayout({
      date_from: periodFrom || undefined,
      date_to: periodTo || undefined,
    }),
    enabled: isOwner && !!(periodFrom || periodTo) || isOwner,
    staleTime: 30_000,
  });

  // Дефолт периода: с последней выплаты + 1 (или начало года).
  useEffect(() => {
    if (!listQ.data || periodFrom || periodTo) return;
    const last = listQ.data.filter(p => p.kind === "regular" && p.period_to)
                  .map(p => p.period_to as string).sort().pop();
    const today = new Date().toISOString().slice(0, 10);
    if (last) {
      setPeriodFrom(addDays(last, 1)); setPeriodTo(today);
    } else {
      setPeriodFrom(`${new Date().getFullYear()}-01-01`); setPeriodTo(today);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQ.data]);

  // Дефолт собственников: подставить из payroll-конфига равными долями.
  useEffect(() => {
    if (owners.length > 0 || ownerCandidates.length === 0) return;
    const each = +(100 / ownerCandidates.length).toFixed(2);
    setOwners(ownerCandidates.map(c => ({
      user_id: c.user_id, name: c.display_name || c.email,
      share_pct: each, avatar_filename: c.avatar_filename,
    })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerCandidates]);

  if (!isOwner) {
    return (
      <FinanceLayout>
        <div className="card muted" style={{ padding: 32, textAlign: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="lock" size={14}/> Доступ только у собственников</span>
        </div>
      </FinanceLayout>
    );
  }

  const snap = previewQ.data;
  const npGross = snap?.totals.net_profit ?? 0;

  // По умолчанию НИЧЕГО не отчисляем в фонды. Юзер сам решает по каждому
  // фонду: положить план (% от ЧП), больше/меньше, или ноль. Кнопка
  // «Применить план» рядом с inputом заполняет план для конкретного фонда,
  // «План для всех» — для всех сразу.
  // (Раньше fundOverrides автоматически заполнялись планом — это вводило в
  // заблуждение: даже если юзер не трогал, фонды списывались.)

  const totalFundAllocations = Array.from(fundOverrides.values())
    .reduce((s, v) => s + (Number(v) || 0), 0);
  const npAfterFunds = Math.max(0, npGross - totalFundAllocations);
  const np = npAfterFunds;  // для совместимости с существующим кодом ниже

  const totalPct = owners.reduce((s, o) => s + (Number(o.share_pct) || 0), 0);
  // Кнопку не гейтим по ЧП полного превью: фиксация считается по принятым
  // к учёту (может быть в плюсе при отрицательном полном нетто). Валидность
  // сумм проверяет сама модалка (canSubmit).
  const canFix = !!snap && (owners.length === 0 || Math.abs(totalPct - 100) < 0.05);

  return (
    <FinanceLayout>
      {/* ── РАСЧЁТНЫЙ ЛИСТ ───────────────────────────────────────── */}
      <div className="card dividend-sheet" style={{ padding: 18, marginBottom: "var(--gap)", display: "flex", flexDirection: "column", gap: 18 }}>

        {/* Шапка */}
        <div className="no-print" style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <Icon name="lock" size={20}/>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Расчётный лист дивидендов</div>
            <div style={{ fontSize: 12, color: "var(--mute)" }}>
              Состояние на сейчас. Чтобы зафиксировать выплату — кнопка справа.
              Снимок будет неизменяем после фиксации.
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button className="btn btn-sm" onClick={() => window.print()} title="Печать расчётного листа">
              <Icon name="report" size={12}/> Печать
            </button>
            <button className="btn btn-primary" disabled={!canFix}
                    onClick={() => setConfirmOpen(true)}>
              <Icon name="plus" size={12}/> Зафиксировать выплату
            </button>
          </div>
        </div>

        {/* Параметры: период + собственники с долями */}
        <div className="dividend-section">
          <SectionTitle>Параметры расчёта</SectionTitle>
          <div className="filter-bar no-print" style={{ gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: "var(--mute)" }}>Период:</span>
            <PeriodChips from={periodFrom} to={periodTo} onChange={(f, t) => { setPeriodFrom(f); setPeriodTo(t); }}/>
            <span style={{ fontSize: 12, color: "var(--mute)", marginLeft: 6 }}>или вручную:</span>
            <input className="input-sm" type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)}/>
            <span>—</span>
            <input className="input-sm" type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)}/>
            <div style={{ width: 1, height: 20, background: "var(--line)", margin: "0 6px" }}/>
            <span style={{ fontSize: 12, color: "var(--mute)" }}>Объекты:</span>
            <button type="button" className={`chip ${objStatusFilter === "all" ? "active" : ""}`}
                    onClick={() => setObjStatusFilter("all")}>Все</button>
            <button type="button" className={`chip ${objStatusFilter === "done" ? "active" : ""}`}
                    onClick={() => setObjStatusFilter("done")}>Завершённые</button>
            <button type="button" className={`chip ${objStatusFilter === "wip" ? "active" : ""}`}
                    onClick={() => setObjStatusFilter("wip")}>Незавершённые</button>
            <div style={{ width: 1, height: 20, background: "var(--line)", margin: "0 6px" }}/>
            <span style={{ fontSize: 12, color: "var(--mute)" }}>Учёт:</span>
            <button type="button" className={`chip ${distFilter === "all" ? "active" : ""}`}
                    onClick={() => setDistFilter("all")}>Все</button>
            <button type="button" className={`chip ${distFilter === "full" ? "active" : ""}`}
                    onClick={() => setDistFilter("full")}>Учтён</button>
            <button type="button" className={`chip ${distFilter === "partial" ? "active" : ""}`}
                    onClick={() => setDistFilter("partial")}>Частично</button>
            <button type="button" className={`chip ${distFilter === "none" ? "active" : ""}`}
                    onClick={() => setDistFilter("none")}>Не учтён</button>
          </div>
          <div className="only-print" style={{ display: "none", fontSize: 13, marginBottom: 8 }}>
            <b>Период расчёта:</b> {periodFrom || "—"} — {periodTo || "—"}
          </div>
          <OwnersEditor owners={owners} setOwners={setOwners} autoFromConfigs={ownerCandidates}
                        readonly={!canEditShares}/>
        </div>

        {/* Итоговые KPI */}
        {previewQ.isLoading ? (
          <div className="muted" style={{ padding: 10 }}>Считаем расчёт…</div>
        ) : previewQ.error ? (
          <div className="alert alert-red"><div className="alert-body">{(previewQ.error as Error).message}</div></div>
        ) : snap && (
          <>
            <div className="dividend-section">
              <SectionTitle>Итоги</SectionTitle>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                <Kpi label="Завершённых объектов" value={String(snap.revenues_by_object.length)}/>
                <Kpi label="Выручка" value={rub(snap.totals.revenue)} color="var(--green)"
                     sub="приход по завершённым объектам в периоде"/>
                <Kpi label="Расходы по объектам" value={rub(snap.totals.expense_objects)} color="var(--red)"
                     sub="ФОТ, материалы, аутсорс, налоги"/>
                <Kpi label="Накладные (коммерч.)" value={rub(snap.totals.overhead)} color="var(--red)"
                     sub="маркетинг + ОП + прочие"/>
                <Kpi label="Общий расход" value={rub(snap.totals.expense_objects + snap.totals.overhead)} color="var(--red)"/>
                <Kpi label="ЧИСТАЯ ПРИБЫЛЬ" value={rub(snap.totals.net_profit)}
                     color={snap.totals.net_profit >= 0 ? "var(--green)" : "var(--red)"} strong/>
                {totalFundAllocations > 0 && (
                  <Kpi label="− в фонды" value={`−${rub(totalFundAllocations)}`} color="#7A4E12"
                       sub="отчисления до раздачи партнёрам"/>
                )}
                {totalFundAllocations > 0 && (
                  <Kpi label="К РАСПРЕДЕЛЕНИЮ" value={rub(npAfterFunds)} color="var(--green)" strong/>
                )}
                {snap.totals.frozen_wip > 0 && (
                  <Kpi label="Заморожено на WIP" value={rub(snap.totals.frozen_wip)} color="#a85f12"
                       sub={`${snap.frozen_wip.count} расходов на ещё не завершённых объектах`}/>
                )}
              </div>
            </div>

            {/* Распределение в фонды — отдельный блок перед партнёрами */}
            {(snap.fund_allocations_planned || []).length > 0 && (
              <div className="dividend-section">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <SectionTitle>
                    Отчисление в фонды
                    {totalFundAllocations > 0 && <span style={{ color: "var(--brick-deep)", fontWeight: 700 }}> — итого {rub(totalFundAllocations)}</span>}
                  </SectionTitle>
                  <span style={{ flex: 1 }} />
                  <button type="button" className="btn btn-sm no-print"
                    onClick={() => {
                      const m = new Map<number, number>();
                      for (const p of (snap.fund_allocations_planned || [])) m.set(p.fund_id, p.amount);
                      setFundOverrides(m);
                    }}
                    title="Подставить плановые суммы во все фонды (% от ЧП)">
                    Применить план для всех
                  </button>
                  {totalFundAllocations > 0 && (
                    <button type="button" className="btn btn-sm no-print"
                      onClick={() => setFundOverrides(new Map())}
                      title="Не отчислять в фонды (все суммы = 0)">
                      Очистить
                    </button>
                  )}
                </div>
                <div style={{ padding: 12, background: "var(--brick-50)", border: "1px solid var(--brick-100)", borderRadius: 6 }}>
                  <table className="tbl tbl-tight" style={{ fontSize: 13, width: "100%" }}>
                    <thead>
                      <tr>
                        <th>Фонд</th>
                        <th className="num" style={{ width: 110 }}>Накоплено</th>
                        <th className="num" style={{ width: 80 }}>% от ЧП</th>
                        <th className="num" style={{ width: 130 }}>План</th>
                        <th className="num" style={{ width: 170 }}>К отчислению</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(snap.fund_allocations_planned || []).map(p => {
                        // Дефолт = 0 (юзер не трогал). Кнопка «План» рядом ставит p.amount.
                        const cur = fundOverrides.get(p.fund_id) ?? 0;
                        const diff = cur - p.amount;
                        return (
                          <tr key={p.fund_id}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{p.fund_name}</div>
                              <div style={{ fontSize: 11, color: "var(--mute)" }}>
                                {p.annual_yield_pct}% год · {p.term_months} мес
                              </div>
                            </td>
                            <td className="num tabular" style={{ color: "var(--ink-3)" }}>
                              {rub(p.current_balance || 0)}
                              <div style={{ fontSize: 10, color: "var(--mute)" }}>уже в фонде</div>
                            </td>
                            <td className="num tabular">{p.share_pct.toFixed(1)}%</td>
                            <td className="num tabular" style={{ color: "var(--mute)" }}>
                              <div>{rub(p.amount)}</div>
                              {cur !== p.amount && p.amount > 0 && (
                                <div style={{ textAlign: "right" }}>
                                  <button type="button"
                                    onClick={() => setFundOverrides(prev => {
                                      const n = new Map(prev);
                                      n.set(p.fund_id, p.amount);
                                      return n;
                                    })}
                                    style={{
                                      marginTop: 2, fontSize: 10,
                                      background: "none", border: "none", padding: 0,
                                      color: "var(--green)", cursor: "pointer",
                                      textDecoration: "underline",
                                    }}
                                    title="Применить план"
                                  >применить</button>
                                </div>
                              )}
                            </td>
                            <td className="num no-print">
                              <input
                                type="number" className="input-sm"
                                step="any" min="0"
                                value={cur}
                                onChange={e => {
                                  const v = parseFloat(e.target.value) || 0;
                                  setFundOverrides(prev => {
                                    const n = new Map(prev);
                                    n.set(p.fund_id, v);
                                    return n;
                                  });
                                }}
                                style={{ width: "100%", textAlign: "right", fontWeight: 600 }}
                                title={diff !== 0 ? `Отклонение от плана: ${diff > 0 ? "+" : ""}${rub(diff)}` : undefined}
                              />
                              {diff !== 0 && (
                                <div style={{ fontSize: 11, color: diff > 0 ? "var(--red)" : "var(--green)", textAlign: "right", marginTop: 2 }}>
                                  {diff > 0 ? "+" : ""}{rub(diff)} к плану
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ fontWeight: 700, borderTop: "2px solid var(--brick-100)" }}>
                        <td colSpan={4}>Итого в фонды</td>
                        <td className="num tabular" style={{ color: "var(--brick-deep)" }}>{rub(totalFundAllocations)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="no-print" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, fontSize: 11, color: "var(--mute)" }}>
                      Кнопка «Сохранить» переведёт суммы в фонды СРАЗУ как ручные зачисления (вне фиксации выплаты).
                      Иначе суммы попадут в фонды при фиксации выплаты.
                    </div>
                    <SaveManualFundsButton
                      allocations={(snap.fund_allocations_planned || []).map(p => ({
                        fund_id:   p.fund_id,
                        fund_name: p.fund_name,
                        amount:    fundOverrides.get(p.fund_id) ?? 0,
                      })).filter(a => a.amount > 0)}
                      onSaved={() => {
                        previewQ.refetch();
                        // Сброс overrides — после save новые балансы видны в "Накоплено"
                        setFundOverrides(new Map());
                      }}
                    />
                  </div>
                </div>
                {/* История ручных перечислений из дивидендов (manual allocations) */}
                <ManualFundAllocationsList fundIds={(snap.fund_allocations_planned || []).map(p => p.fund_id)} />
              </div>
            )}

            {/* Распределение по партнёрам */}
            {owners.length > 0 && (
              <div className="dividend-section">
                <SectionTitle>Распределение между партнёрами</SectionTitle>
                <div style={{ padding: 12, background: "rgba(46,125,50,0.06)", border: "1px solid rgba(46,125,50,0.22)", borderRadius: 8 }}>
                  <table className="tbl tbl-tight" style={{ fontSize: 13, width: "100%" }}>
                    <thead>
                      <tr><th>Партнёр</th><th className="num" style={{ width: 100 }}>Доля</th><th className="num" style={{ width: 160 }}>К выплате</th></tr>
                    </thead>
                    <tbody>
                      {owners.map((o, i) => {
                        const amt = np > 0 && totalPct > 0 ? np * (Number(o.share_pct) || 0) / totalPct : 0;
                        return (
                          <tr key={i}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <Avatar name={o.name} userId={o.user_id || undefined} avatarFilename={o.avatar_filename || null} size={22} color="#4fae4e"/>
                                {o.name}
                              </div>
                            </td>
                            <td className="num tabular">{(Number(o.share_pct) || 0).toFixed(1)}%</td>
                            <td className="num tabular strong" style={{ color: amt > 0 ? "var(--green)" : "var(--mute)" }}>{rub(amt)}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                        <td>Итого</td>
                        <td className="num tabular" style={{ color: totalPct === 100 ? "var(--green)" : "var(--red)" }}>{totalPct.toFixed(1)}%</td>
                        <td className="num tabular" style={{ color: "var(--green)" }}>{rub(np > 0 ? np : 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <SnapshotSections snap={snap} statusFilter={objStatusFilter} distFilter={distFilter}
                              canEditStatus={canEditShares}
                              onStatusChanged={() => previewQ.refetch()}/>
          </>
        )}
      </div>

      {/* ── ИСТОРИЯ ВЫПЛАТ ──────────────────────────────────────── */}
      <div className="card no-print">
        <div style={{ padding: "14px 16px 6px", fontSize: 14, fontWeight: 700 }}>
          История выплат ({listQ.data?.length ?? 0})
        </div>
        {listQ.isLoading ? (
          <div className="muted" style={{ padding: 20 }}>Загрузка…</div>
        ) : listQ.error ? (
          <div className="alert alert-red"><div className="alert-body">{(listQ.error as Error).message}</div></div>
        ) : (listQ.data ?? []).length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--mute)" }}>
            Пока выплат не было. Нажмите «Зафиксировать выплату» наверху, чтобы сохранить первую.
          </div>
        ) : (
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Дата выплаты</th>
                  <th style={{ width: 90 }}>Тип</th>
                  <th>Период / основание</th>
                  <th className="num" style={{ width: 130 }}>Выручка</th>
                  <th className="num" style={{ width: 130 }}>Расходы</th>
                  <th className="num" style={{ width: 140 }}>ЧП</th>
                  <th className="num" style={{ width: 140 }}>На партнёра</th>
                  <th>Заметка</th>
                  <th style={{ width: 60 }}/>
                </tr>
              </thead>
              <tbody>
                {(listQ.data ?? []).map(p => (
                  <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(p.id)}>
                    <td className="strong">{fmtDate(p.paid_at)}</td>
                    <td>
                      {p.kind === "top_up"
                        ? <span className="badge" style={{ background: "rgba(232,200,80,0.30)", color: "#a85f12" }}>Доплата</span>
                        : <span className="badge">Обычная</span>}
                    </td>
                    <td>
                      {p.kind === "top_up"
                        ? <span style={{ fontSize: 12, color: "var(--mute)" }}>доплата к выплате #{p.parent_payout_id}</span>
                        : (p.period_from || p.period_to
                          ? `${fmtDate(p.period_from)} — ${fmtDate(p.period_to)}`
                          : <span className="muted">всё время</span>)}
                    </td>
                    <td className="num tabular" style={{ color: "var(--green)" }}>{rub(p.total_revenue)}</td>
                    <td className="num tabular" style={{ color: "var(--red)" }}>
                      {rub(p.total_expense_objects + p.total_overhead)}
                    </td>
                    <td className="num tabular strong" style={{ color: p.net_profit >= 0 ? "var(--green)" : "var(--red)" }}>
                      {p.net_profit >= 0 ? "" : "−"}{rub(Math.abs(p.net_profit))}
                    </td>
                    <td className="num tabular strong">
                      {rub(p.per_owner)} <span style={{ color: "var(--mute)", fontSize: 11 }}>× {p.owners_count}</span>
                    </td>
                    <td style={{ fontSize: 12, color: "var(--mute)" }}>{p.note || "—"}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn btn-sm" title="Удалить" style={{ color: "var(--red)" }}
                              onClick={async () => {
                                if (!confirm(`Удалить выплату от ${fmtDate(p.paid_at)}?`)) return;
                                try { await deleteDividendPayout(p.id); listQ.refetch(); previewQ.refetch(); }
                                catch (e) { alert("Ошибка: " + (e as Error).message); }
                              }}>
                        <Icon name="trash" size={12}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmOpen && snap && (
        <ConfirmFixModal
          snap={snap} owners={owners} totalPct={totalPct}
          periodFrom={periodFrom} periodTo={periodTo}
          fundAllocations={Array.from(fundOverrides.entries())
            .filter(([, amt]) => amt > 0)
            .map(([fund_id, amount]) => ({ fund_id, amount }))}
          npAfterFunds={npAfterFunds}
          onClose={() => setConfirmOpen(false)}
          onCreated={(id) => {
            setConfirmOpen(false);
            listQ.refetch();
            previewQ.refetch();
            setOpenId(id);
          }}/>
      )}
      {openId !== null && (
        <PayoutDetailModal id={openId} onClose={() => setOpenId(null)}
                           onChanged={() => { listQ.refetch(); previewQ.refetch(); }}/>
      )}
    </FinanceLayout>
  );
}

/* ─── KPI плитка ─────────────────────────────────────────────────── */
function Kpi({ label, value, color, strong, sub, accent }: {
  label: string; value: string; color?: string; strong?: boolean; sub?: string; accent?: boolean;
}) {
  return (
    <div style={{ padding: 10, borderRadius: 8,
      background: accent ? "rgba(46,125,50,0.08)" : "var(--paper-2)",
      border: accent ? "1px solid rgba(46,125,50,0.30)" : undefined,
    }}>
      <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div className="tabular" style={{ fontSize: strong ? 18 : 14, fontWeight: strong ? 700 : 500, marginTop: 4, color }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase",
                  letterSpacing: 0.4, marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid var(--line)" }}>
      {children}
    </div>
  );
}

/** Сворачиваемая секция расчётного листа. Клик по заголовку — свернуть/развернуть. */
function CollapsibleSection({ title, defaultOpen = false, children }: {
  title: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="dividend-section">
      <div onClick={() => setOpen(o => !o)} style={{ cursor: "pointer", userSelect: "none" }}
           title={open ? "Свернуть" : "Развернуть"}>
        <SectionTitle>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon name={open ? "chevron_d" : "chevron_r"} size={11}/>
            {title}
          </span>
        </SectionTitle>
      </div>
      {open && children}
    </div>
  );
}

/* ─── Редактор владельцев + долей ──────────────────────────────────
 *  readonly=true (для не-главного-админа): inputs disabled, кнопки скрыты.
 *  Доли могут менять только Геннадий (role='admin' + is_superuser=true).
 */
function OwnersEditor({ owners, setOwners, autoFromConfigs, readonly }: {
  owners: OwnerShare[]; setOwners: (v: OwnerShare[]) => void; autoFromConfigs: PayrollConfigRow[];
  readonly?: boolean;
}) {
  function add(c: PayrollConfigRow) {
    setOwners([...owners, { user_id: c.user_id, name: c.display_name || c.email, share_pct: 0, avatar_filename: c.avatar_filename }]);
  }
  function remove(i: number) { setOwners(owners.filter((_, x) => x !== i)); }
  function updatePct(i: number, v: number) { setOwners(owners.map((o, x) => x === i ? { ...o, share_pct: v } : o)); }
  function equalSplit() {
    const each = +(100 / Math.max(owners.length, 1)).toFixed(2);
    setOwners(owners.map(o => ({ ...o, share_pct: each })));
  }
  const totalPct = owners.reduce((s, o) => s + (Number(o.share_pct) || 0), 0);
  const available = autoFromConfigs.filter(c => !owners.some(o => o.user_id === c.user_id));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: "var(--mute)" }}>
          Собственники и доли (%)
          {readonly && <span style={{ marginLeft: 8, color: "var(--mute-2)", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 4 }}>· <Icon name="lock" size={10}/> редактирует только главный администратор</span>}
        </div>
        {!readonly && (
          <button type="button" className="btn btn-sm no-print" style={{ marginLeft: "auto" }} onClick={equalSplit}>
            Поровну
          </button>
        )}
      </div>
      <table className="tbl tbl-tight" style={{ fontSize: 13, width: "100%" }}>
        <tbody>
          {owners.map((o, i) => (
            <tr key={i}>
              <td>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Avatar name={o.name} userId={o.user_id || undefined} avatarFilename={o.avatar_filename || null} size={22} color="#4fae4e"/>
                  {o.name}
                </div>
              </td>
              <td style={{ width: 110 }}>
                {readonly ? (
                  <div className="tabular" style={{ textAlign: "right", paddingRight: 8, fontWeight: 600 }}>
                    {(Number(o.share_pct) || 0).toFixed(2)}%
                  </div>
                ) : (
                  <input className="input-sm" type="number" step="0.01" min={0} max={100}
                         value={o.share_pct}
                         onChange={e => updatePct(i, Number(e.target.value) || 0)}/>
                )}
              </td>
              <td style={{ width: 36 }} className="no-print">
                {!readonly && (
                  <button type="button" className="btn btn-sm" style={{ color: "var(--red)" }} onClick={() => remove(i)}>
                    <Icon name="x" size={11}/>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, fontSize: 12 }}>
        <span style={{ color: totalPct === 100 ? "var(--green)" : "var(--red)" }}>Σ долей: {totalPct.toFixed(2)}%</span>
        {totalPct !== 100 && <span style={{ color: "var(--mute)" }}>{totalPct < 100 ? "не хватает" : "перебор"}</span>}
        {!readonly && available.length > 0 && (
          <select className="input-sm no-print" style={{ marginLeft: "auto", minWidth: 220 }}
                  onChange={e => { const c = available.find(x => x.user_id === e.target.value); if (c) add(c); e.target.value = ""; }}
                  defaultValue="">
            <option value="">+ добавить собственника</option>
            {available.map(c => <option key={c.user_id} value={c.user_id}>{c.display_name || c.email}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

/* ─── Полные секции снимка (используются и в листе, и в детали) ──── */
/** Чип/редактор статуса «учтён в распределении».
 *  canEdit=true (главный админ): клик по чипу → popover со списком
 *  статусов, выбор + галочка = сохранить. Без постоянного селекта. */
const DIST_STATUS_STYLE: Record<string, React.CSSProperties> = {
  none:    { background: "rgba(179,38,30,0.10)", color: "var(--red)" },
  partial: { background: "rgba(212,167,58,0.15)", color: "#8a6e10" },
  full:    { background: "rgba(46,125,50,0.10)", color: "var(--green)" },
};

function DistStatusCell({ ds, objectId, canEdit, onChanged }: {
  ds: PayoutObjectDistributionStatus | undefined;
  objectId: number;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // draft: "" = авто, иначе выбранный ручной статус
  const [draft, setDraft] = useState<"" | "none" | "partial" | "full">("");
  // Позиция попапа: fixed-координаты от чипа. Портал в body — чтобы не
  // обрезался overflow'ом таблицы/модалки (см. memory: lightbox portal).
  const chipRef = useRef<HTMLSpanElement>(null);
  const [popPos, setPopPos] = useState<{ top: number; left: number } | null>(null);
  const POP_W = 220;
  const status = ds?.status ?? "none";
  const label = status === "full"
    ? `Полностью${ds ? ` · ${rub(ds.distributed_before)}` : ""}`
    : status === "partial"
      ? `Частично · осталось ${ds ? rub(ds.remaining) : "?"}`
      : "Не учтён";

  function toggleOpen(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setDraft(ds?.is_manual ? status : "");
    const r = chipRef.current?.getBoundingClientRect();
    if (r) {
      // Выравниваем по правому краю чипа; не даём вылезти за экран
      const left = Math.max(8, Math.min(r.right - POP_W, window.innerWidth - POP_W - 8));
      const top = Math.min(r.bottom + 4, window.innerHeight - 240);
      setPopPos({ top, left });
    }
    setOpen(true);
  }

  const chip = (
    <span
      ref={chipRef}
      onClick={canEdit ? toggleOpen : undefined}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
        cursor: canEdit ? "pointer" : undefined,
        ...DIST_STATUS_STYLE[status],
      }}
      title={ds
        ? `Уже учтено: ${rub(ds.distributed_before)} · Осталось: ${rub(ds.remaining)}${ds.is_manual ? " · статус изменён вручную" : ""}${canEdit ? " · клик — изменить" : ""}`
        : undefined}
    >
      {label}{ds?.is_manual && <span style={{ fontSize: 9, opacity: 0.7 }}>✎</span>}
      {canEdit && <Icon name="chevron_d" size={9}/>}
    </span>
  );
  if (!canEdit) return chip;

  const OPTIONS: { value: "" | "none" | "partial" | "full"; label: string }[] = [
    { value: "",        label: "Авто (по истории выплат)" },
    { value: "none",    label: "Не учтён" },
    { value: "partial", label: "Учтён частично" },
    { value: "full",    label: "Учтён полностью" },
  ];

  async function save() {
    setSaving(true);
    try {
      await setObjectDistributionStatus(objectId, draft === "" ? null : draft);
      setOpen(false);
      onChanged();
    } catch (err) {
      alert("Ошибка: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {chip}
      {open && popPos && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 990 }}/>
          <div style={{
            position: "fixed", top: popPos.top, left: popPos.left, zIndex: 1000,
            width: POP_W,
            background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.18)", padding: 6,
          }}>
            {OPTIONS.map(opt => (
              <div key={opt.value}
                   onClick={() => setDraft(opt.value)}
                   style={{
                     padding: "6px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                     display: "flex", alignItems: "center", gap: 8,
                     background: draft === opt.value ? "var(--paper-2)" : "transparent",
                     fontWeight: draft === opt.value ? 700 : 400,
                   }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                  background: opt.value === "" ? "var(--mute)" : (DIST_STATUS_STYLE[opt.value].color as string),
                }}/>
                {opt.label}
                {draft === opt.value && <span style={{ marginLeft: "auto", display: "inline-flex" }}><Icon name="check" size={12}/></span>}
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line)" }}>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(false)} disabled={saving}>
                <Icon name="x" size={11}/>
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}
                      title="Сохранить статус">
                {saving ? "…" : <Icon name="check" size={12}/>}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

/** Ячейка «К учёту»: выписка + новые приходы + кнопки принятия (главный админ). */
void ToAccountCell;
function ToAccountCell({ ds, objectId, canEdit, onChanged }: {
  ds: PayoutObjectDistributionStatus | undefined;
  objectId: number;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  if (!ds) return <span className="muted">—</span>;
  const toAccount = ds.to_account || 0;
  const newUnaccepted = ds.new_unaccepted || 0;
  const hasPayout = !!ds.last_payout_id;

  async function accept() {
    setSaving(true);
    try {
      await acceptObjectDistribution(objectId);
      onChanged();
    } catch (e) {
      alert("Ошибка: " + (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11 }}>
      {hasPayout && (
        <span style={{ fontWeight: 700, color: "var(--ink-1)", whiteSpace: "nowrap" }}>
          ВЫПИСКА #{ds.last_payout_id}
        </span>
      )}
      {toAccount > 0 && (
        <span style={{ fontWeight: 700, color: "var(--green)", whiteSpace: "nowrap" }}>
          К УЧЁТУ {rub(toAccount)}
        </span>
      )}
      {newUnaccepted > 0 && (
        <span style={{ color: "#8a6e10", lineHeight: 1.3 }}>
          {hasPayout ? "Новый приход" : "Не принят"} на <b className="tabular">{rub(newUnaccepted)}</b>
          {canEdit && (
            <button type="button" className="no-print"
                    disabled={saving}
                    onClick={e => { e.stopPropagation(); accept(); }}
                    title={hasPayout ? "Добавить новый приход к учёту" : "Принять всю выручку объекта к учёту"}
                    style={{
                      display: "block", marginTop: 2,
                      background: "rgba(46,125,50,0.10)", border: "1px solid rgba(46,125,50,0.30)",
                      color: "var(--green)", borderRadius: 8, padding: "1px 8px",
                      fontSize: 10, fontWeight: 700, cursor: "pointer",
                    }}>
              {saving ? "…" : hasPayout ? "Добавить к учёту?" : "Принять к учёту"}
            </button>
          )}
        </span>
      )}
      {!hasPayout && toAccount === 0 && newUnaccepted === 0 && (
        <span className="muted">—</span>
      )}
    </div>
  );
}

/** Ячейка «К учёту»: выписка / новый приход / кнопка принятия.
 *  Кнопки видит только главный админ (canEdit). */
function AcceptCell({ ds, objectId, canEdit, onChanged }: {
  ds: PayoutObjectDistributionStatus | undefined;
  objectId: number;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  if (!ds) return <span className="muted">—</span>;
  const toAccount = ds.to_account || 0;
  const newUnaccepted = ds.new_unaccepted || 0;
  const hasPayout = !!ds.last_payout_id;

  async function accept() {
    if (!confirm(`Принять к учёту маржу ${rub(newUnaccepted)} по этому объекту?`)) return;
    setSaving(true);
    try {
      await acceptObjectDistribution(objectId);
      onChanged();
    } catch (err) {
      alert("Ошибка: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function cancelAccept() {
    if (!confirm(`Отменить принятие к учёту ${rub(toAccount)}? Сумма вернётся в «не принято».`)) return;
    setSaving(true);
    try {
      await cancelObjectDistributionAcceptance(objectId);
      onChanged();
    } catch (err) {
      alert("Ошибка: " + (err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
      {hasPayout && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 8,
          background: "var(--paper-2)", color: "var(--mute)", whiteSpace: "nowrap",
          border: "1px solid var(--line)", letterSpacing: 0.3,
        }}>ВЫПИСКА #{ds.last_payout_id}</span>
      )}
      {toAccount > 0 && (
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--green)", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>
          К УЧЁТУ {rub(toAccount)}
          {canEdit && (
            <button type="button" className="no-print"
                    disabled={saving}
                    onClick={e => { e.stopPropagation(); void cancelAccept(); }}
                    title="Отменить принятие к учёту"
                    style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                      color: "var(--mute)", display: "inline-flex", alignItems: "center",
                    }}>
              <Icon name="x" size={11}/>
            </button>
          )}
        </span>
      )}
      {newUnaccepted > 0 && (
        <div style={{ fontSize: 10.5, color: "#8a6e10", lineHeight: 1.35 }}>
          {hasPayout
            ? <>К распределению новая маржа на сумму <b className="tabular">{rub(newUnaccepted)}</b>.</>
            : <>Маржа не принята к учёту: <b className="tabular">{rub(newUnaccepted)}</b>.</>}
          {canEdit && (
            <button type="button" className="no-print"
                    disabled={saving}
                    onClick={e => { e.stopPropagation(); void accept(); }}
                    style={{
                      display: "block", marginTop: 2, fontSize: 10, fontWeight: 700,
                      background: "rgba(46,125,50,0.10)", border: "1px solid rgba(46,125,50,0.30)",
                      color: "var(--green)", borderRadius: 8, padding: "2px 8px", cursor: "pointer",
                    }}>
              {saving ? "…" : hasPayout ? "Добавить к учёту" : "Принять к учёту"}
            </button>
          )}
        </div>
      )}
      {!hasPayout && toAccount === 0 && newUnaccepted === 0 && <span className="muted" style={{ fontSize: 11 }}>—</span>}
    </div>
  );
}

function SnapshotSections({ snap, statusFilter = "all", distFilter = "all", canEditStatus = false, onStatusChanged }: {
  snap: PayoutSnapshot;
  /** all — все объекты; done — только завершённые; wip — только в работе. */
  statusFilter?: "all" | "done" | "wip";
  /** Фильтр по статусу распределения: all / full / partial / none. */
  distFilter?: "all" | "full" | "partial" | "none";
  canEditStatus?: boolean;
  onStatusChanged?: () => void;
}) {
  const marginByObject = useMemo(() => {
    const m = new Map<number, { object_id: number; object_name: string; region: string | null; rev: number; exp: number; wip: boolean }>();
    for (const r of snap.revenues_by_object) {
      m.set(r.object_id, { object_id: r.object_id, object_name: r.object_name, region: r.region, rev: r.total, exp: 0, wip: false });
    }
    for (const e of snap.expenses_by_object) {
      const cur = m.get(e.object_id) ?? { object_id: e.object_id, object_name: e.object_name, region: e.region, rev: 0, exp: 0, wip: false };
      cur.exp = e.total;
      m.set(e.object_id, cur);
    }
    // Незавершённые объекты (WIP) — приходы + замороженные расходы
    for (const w of (snap.wip_by_object || [])) {
      m.set(w.object_id, { object_id: w.object_id, object_name: w.object_name, region: w.region, rev: w.rev, exp: w.exp, wip: true });
    }
    return Array.from(m.values()).sort((a, b) => (b.rev - b.exp) - (a.rev - a.exp));
  }, [snap]);
  const shownObjects = useMemo(() => {
    let list = marginByObject;
    if (statusFilter === "done") list = list.filter(m => !m.wip);
    else if (statusFilter === "wip") list = list.filter(m => m.wip);
    if (distFilter !== "all") {
      const st = snap.object_distribution_status || [];
      list = list.filter(m => {
        const d = st.find(x => x.object_id === m.object_id);
        return (d?.status ?? "none") === distFilter;
      });
    }
    return list;
  }, [marginByObject, statusFilter, distFilter, snap]);
  const totalRev = snap.totals.revenue;
  const totalExp = snap.totals.expense_objects;
  const doneCount = marginByObject.filter(m => !m.wip).length;
  const wipCount = marginByObject.filter(m => m.wip).length;

  // «К расчёту» = ТОЛЬКО принятое к учёту (Σ to_account: принято главным
  // админом, но ещё не выплачено). Не принятые новые приходы — справочно.
  const toCalc = useMemo(() => {
    const ids = new Set(marginByObject.map(m => m.object_id));
    let accepted = 0;
    let unaccepted = 0;
    for (const d of (snap.object_distribution_status || [])) {
      if (!ids.has(d.object_id)) continue;
      accepted += d.to_account || 0;
      unaccepted += d.new_unaccepted || 0;
    }
    return { accepted, unaccepted, total: accepted };
  }, [snap, marginByObject]);

  return (
    <>
      {snap.frozen_wip && snap.frozen_wip.count > 0 && (
        <div className="alert alert-amber dividend-section" style={{ margin: 0 }}>
          <div className="alert-body">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, verticalAlign: "middle" }}><Icon name="warn" size={13}/><b>Замороженные расходы:</b></span> в периоде на ещё не завершённые объекты потрачено{" "}
            <b>{rub(snap.frozen_wip.total)}</b> ({snap.frozen_wip.count} расходов).
            Эти деньги ушли, но в маржу пока не входят (объекты не завершены).
            Учитывайте при принятии решения о выплате.
          </div>
        </div>
      )}

      <div className="dividend-section">
        <SectionTitle>
          Объекты и маржа ({shownObjects.length}{statusFilter === "all" ? ` = ${doneCount} завершённых + ${wipCount} в работе` : ""})
        </SectionTitle>
        {shownObjects.length === 0 ? (
          <div className="muted" style={{ padding: 8 }}>Нет данных.</div>
        ) : (
          <table className="tbl" style={{ fontSize: 12, width: "100%" }}>
            <thead><tr>
              <th>Клиент</th><th style={{ width: 90 }}>Статус</th><th>Регион</th>
              <th className="num" style={{ width: 105 }} title="Сумма договора объекта">Договор</th>
              <th className="num" style={{ width: 105 }}>Приход</th>
              <th className="num" style={{ width: 105 }}>Расход</th>
              <th className="num" style={{ width: 110 }}>Маржа</th>
              <th className="num" style={{ width: 70 }}>Рент-сть</th>
              <th style={{ width: 95 }} title="Дата последнего прихода по ДДС (за всё время)">Посл. приход</th>
              <th style={{ width: 230 }}>Учтён в распределении</th>
              <th style={{ width: 190 }} title="Выписки и принятие сумм к расчёту">К учёту</th>
            </tr></thead>
            <tbody>
              {shownObjects.map(m => {
                const margin = m.rev - m.exp;
                const pct = m.rev > 0 ? margin / m.rev * 100 : null;
                const ds = (snap.object_distribution_status || []).find(d => d.object_id === m.object_id);
                // Полностью учтён → строка зелёная; WIP → жёлтая
                const rowBg = ds?.status === "full"
                  ? "rgba(46,125,50,0.09)"
                  : m.wip ? "rgba(212,167,58,0.06)" : undefined;
                return (
                  <tr key={m.object_id} style={rowBg ? { background: rowBg } : undefined}>
                    <td className="strong">
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {m.object_name}
                        {ds?.production_project_id && (
                          <a href={`${window.location.protocol}//production.${window.location.hostname.split(".").slice(1).join(".")}/production/${ds.production_project_id}`}
                             target="_blank" rel="noopener noreferrer"
                             onClick={e => e.stopPropagation()}
                             title="Открыть карточку в Производстве"
                             className="no-print"
                             style={{ display: "inline-flex", alignItems: "center", color: "var(--green)" }}>
                            <Icon name="home" size={12}/>
                          </a>
                        )}
                        {ds?.amo_url && (
                          <a href={ds.amo_url} target="_blank" rel="noopener noreferrer"
                             onClick={e => e.stopPropagation()}
                             title="Открыть сделку в AmoCRM"
                             className="no-print"
                             style={{ display: "inline-flex", alignItems: "center", color: "var(--brick)" }}>
                            <Icon name="external" size={12}/>
                          </a>
                        )}
                      </span>
                    </td>
                    <td>
                      {m.wip ? (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(212,167,58,0.18)", color: "#8a6e10", whiteSpace: "nowrap" }}>В работе</span>
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(46,125,50,0.10)", color: "var(--green)", whiteSpace: "nowrap" }}>Завершён</span>
                      )}
                    </td>
                    <td>{m.region || "—"}</td>
                    <td className="num tabular" style={{ fontWeight: 600 }}>{ds?.contract_sum ? rub(ds.contract_sum) : "—"}</td>
                    <td className="num tabular" style={{ color: "var(--green)" }}>{rub(m.rev)}</td>
                    <td className="num tabular" style={{ color: "var(--red)" }}>{rub(m.exp)}</td>
                    <td className="num tabular strong" style={{ color: margin >= 0 ? "var(--green)" : "var(--red)" }}>
                      {margin >= 0 ? "+" : "−"}{rub(Math.abs(margin))}
                      {m.wip && <div style={{ fontSize: 9, fontWeight: 400, color: "var(--mute)" }}>промежуточная</div>}
                    </td>
                    <td className="num tabular" style={{ color: pct !== null && pct < 0 ? "var(--red)" : "var(--ink-1)" }}>
                      {pct !== null ? `${pct.toFixed(1)}%` : "—"}
                    </td>
                    <td className="tabular" style={{ fontSize: 11, color: "var(--mute)" }}>
                      {ds?.last_revenue_at ? fmtDate(ds.last_revenue_at) : "—"}
                    </td>
                    <td>
                      <DistStatusCell ds={ds} objectId={m.object_id} canEdit={canEditStatus}
                                      onChanged={() => onStatusChanged?.()}/>
                    </td>
                    <td>
                      <AcceptCell ds={ds} objectId={m.object_id} canEdit={canEditStatus}
                                  onChanged={() => onStatusChanged?.()}/>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                <td colSpan={3}>Итого (завершённые в маржу)</td>
                <td className="num tabular">
                  {rub(shownObjects.reduce((s, m) => {
                    const d = (snap.object_distribution_status || []).find(x => x.object_id === m.object_id);
                    return s + (d?.contract_sum || 0);
                  }, 0))}
                </td>
                <td className="num tabular" style={{ color: "var(--green)" }}>{rub(totalRev)}</td>
                <td className="num tabular" style={{ color: "var(--red)" }}>{rub(totalExp)}</td>
                <td className="num tabular" style={{ color: (totalRev - totalExp) >= 0 ? "var(--green)" : "var(--red)" }}>
                  {(totalRev - totalExp) >= 0 ? "+" : "−"}{rub(Math.abs(totalRev - totalExp))}
                </td>
                <td className="num tabular">
                  {totalRev > 0 ? `${((totalRev - totalExp) / totalRev * 100).toFixed(1)}%` : "—"}
                </td>
                <td colSpan={3}/>
              </tr>
            </tbody>
          </table>
        )}

        {/* К РАСЧЁТУ = только принятое к учёту (Σ «К УЧЁТУ» из колонки).
            Не принятые новые приходы показываются справочно.
            Итог делится 50/50 между партнёрами. */}
        {(toCalc.total > 0 || toCalc.unaccepted > 0) && (
          <div style={{
            marginTop: 10, padding: "12px 16px", borderRadius: 8,
            background: "rgba(46,125,50,0.06)", border: "1px solid rgba(46,125,50,0.25)",
            display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--green)" }}>
              К расчёту
            </div>
            <div style={{ fontSize: 12, color: "var(--mute)" }}>
              Принято к учёту:{" "}
              <strong className="tabular" style={{ color: "var(--green)", fontSize: 14 }}>{rub(toCalc.accepted)}</strong>
            </div>
            {toCalc.unaccepted > 0 && (
              <div style={{ fontSize: 12, color: "var(--mute)" }}>
                Не принято (новые приходы):{" "}
                <strong className="tabular" style={{ color: "#8a6e10", fontSize: 14 }}>{rub(toCalc.unaccepted)}</strong>
              </div>
            )}
            <div style={{ fontSize: 12, color: "var(--mute)" }}>
              Сумма к расчёту:{" "}
              <strong className="tabular" style={{ color: "var(--green)", fontSize: 16 }}>{rub(toCalc.total)}</strong>
            </div>
            <span style={{ flex: 1 }}/>
            <div style={{ fontSize: 12, color: "var(--mute)", display: "flex", gap: 16 }}>
              <span>50%: <strong className="tabular" style={{ color: "var(--ink-1)", fontSize: 14 }}>{rub(toCalc.total / 2)}</strong></span>
              <span>50%: <strong className="tabular" style={{ color: "var(--ink-1)", fontSize: 14 }}>{rub(toCalc.total / 2)}</strong></span>
            </div>
          </div>
        )}
      </div>

      <CollapsibleSection title={`Приходы — детально по каждому объекту (${snap.revenues_by_object.length} объектов)`} defaultOpen={false}>
        {snap.revenues_by_object.length === 0 ? (
          <div className="muted" style={{ padding: 8 }}>Приходов нет.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {snap.revenues_by_object.map(o => (
              <ObjectGroup key={o.object_id} name={o.object_name} region={o.region} total={o.total} totalColor="var(--green)" kind="income">
                <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
                  <thead><tr>
                    <th style={{ width: 90 }}>Дата</th><th>Контрагент</th>
                    <th style={{ width: 80 }}>Тип</th><th className="num" style={{ width: 110 }}>Сумма</th>
                  </tr></thead>
                  <tbody>
                    {o.lines.map(l => (
                      <tr key={l.id}>
                        <td className="tabular">{fmtDate(l.received_at)}</td>
                        <td>{l.counterparty || <span className="muted">—</span>}</td>
                        <td>{l.type || "—"}</td>
                        <td className="num tabular" style={{ color: "var(--green)" }}>{rub(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ObjectGroup>
            ))}
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection title={`Расходы по объектам — детально (${snap.expenses_by_object.length})`} defaultOpen={false}>
        {snap.expenses_by_object.length === 0 ? (
          <div className="muted" style={{ padding: 8 }}>Расходов нет.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {snap.expenses_by_object.map(o => (
              <ObjectGroup key={o.object_id} name={o.object_name} region={o.region} total={o.total} totalColor="var(--red)" kind="expense">
                <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
                  <thead><tr>
                    <th style={{ width: 90 }}>Дата</th><th>Категория</th><th>Поставщик / описание</th>
                    <th className="num" style={{ width: 110 }}>Сумма</th>
                  </tr></thead>
                  <tbody>
                    {o.lines.map(l => (
                      <tr key={l.id}>
                        <td className="tabular">{fmtDate(l.date)}</td>
                        <td>{l.category_name}<span style={{ fontSize: 10, color: "var(--mute)", marginLeft: 4 }}>{l.kind}</span></td>
                        <td>{l.supplier || l.description || <span className="muted">—</span>}</td>
                        <td className="num tabular" style={{ color: "var(--red)" }}>{rub(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ObjectGroup>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Налоги — отдельный блок (УСН/НДФЛ/страх). Учитываются в total_overhead. */}
      {(() => {
        const taxLines = snap.overhead_lines.taxes || [];
        const taxTotal = snap.overhead_totals.taxes || 0;
        return (
          <CollapsibleSection title={`Налоги (итого ${rub(taxTotal)})`} defaultOpen={false}>
            {taxLines.length === 0 ? (
              <div className="muted" style={{ padding: 8 }}>Налогов в периоде нет.</div>
            ) : (
              <ObjectGroup name="Бюджет / ФНС" region={null}
                           total={taxTotal} totalColor="var(--red)" kind="expense">
                <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
                  <thead><tr>
                    <th style={{ width: 90 }}>Дата</th><th>Категория</th><th>Поставщик / описание</th>
                    <th className="num" style={{ width: 110 }}>Сумма</th>
                  </tr></thead>
                  <tbody>
                    {taxLines.map(l => <TaxLineRow key={l.id} line={l} />)}
                  </tbody>
                </table>
              </ObjectGroup>
            )}
          </CollapsibleSection>
        );
      })()}

      <CollapsibleSection title={`Переменные коммерческие расходы (накладные, итого ${rub((snap.overhead_totals.marketing || 0) + (snap.overhead_totals.sales || 0) + (snap.overhead_totals.other || 0) + (snap.overhead_totals.unassigned_production || 0))})`} defaultOpen={false}>
        {(["marketing", "sales", "other", "unassigned_production"] as const).every(k => (snap.overhead_lines[k] || []).length === 0) ? (
          <div className="muted" style={{ padding: 8 }}>Накладных расходов в периоде нет.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(["marketing", "sales", "other", "unassigned_production"] as const).map(k => {
              const lines = snap.overhead_lines[k] || [];
              if (!lines.length) return null;
              return (
                <ObjectGroup key={k} name={OVERHEAD_LABELS[k]} region={null}
                             total={snap.overhead_totals[k] || 0} totalColor="var(--red)" kind="expense">
                  <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
                    <thead><tr>
                      <th style={{ width: 90 }}>Дата</th><th>Категория</th><th>Поставщик / описание</th>
                      <th className="num" style={{ width: 110 }}>Сумма</th>
                    </tr></thead>
                    <tbody>
                      {lines.map(l => (
                        <tr key={l.id}>
                          <td className="tabular">{fmtDate(l.date)}</td>
                          <td>{l.category_name}</td>
                          <td>{l.supplier || l.description || <span className="muted">—</span>}</td>
                          <td className="num tabular" style={{ color: "var(--red)" }}>{rub(l.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ObjectGroup>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      {snap.frozen_wip && snap.frozen_wip.count > 0 && (
        <CollapsibleSection title={`Замороженные расходы на ещё не завершённых объектах (${snap.frozen_wip.count})`} defaultOpen={false}>
          <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
            <thead><tr>
              <th style={{ width: 90 }}>Дата</th><th>Объект</th><th>Категория</th>
              <th>Поставщик</th><th className="num" style={{ width: 110 }}>Сумма</th>
            </tr></thead>
            <tbody>
              {snap.frozen_wip.lines.map(l => (
                <tr key={l.id}>
                  <td className="tabular">{fmtDate(l.date)}</td>
                  <td>{l.object_name}</td>
                  <td>{l.category_name}<span style={{ fontSize: 10, color: "var(--mute)", marginLeft: 4 }}>{l.kind}</span></td>
                  <td>{l.supplier || l.description || <span className="muted">—</span>}</td>
                  <td className="num tabular" style={{ color: "#a85f12" }}>{rub(l.amount)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                <td colSpan={4}>Итого замороженных WIP</td>
                <td className="num tabular" style={{ color: "#a85f12" }}>{rub(snap.frozen_wip.total)}</td>
              </tr>
            </tbody>
          </table>
        </CollapsibleSection>
      )}
    </>
  );
}

function ObjectGroup({ name, region, total, totalColor, kind, children }: {
  name: string; region: string | null; total: number; totalColor: string;
  /** Тинт фона: income — светло-зелёный, expense — светло-красный, neutral — серый. */
  kind?: "income" | "expense";
  children: React.ReactNode;
}) {
  // Плашки сворачиваемые: по умолчанию свёрнуты (шапка с итогом видна),
  // клик по шапке раскрывает детализацию.
  const [open, setOpen] = useState(false);
  const bg = kind === "income" ? "rgba(46,125,50,0.07)"
           : kind === "expense" ? "rgba(179,38,30,0.07)"
           : "var(--paper-2)";
  const border = kind === "income" ? "1px solid rgba(46,125,50,0.22)"
               : kind === "expense" ? "1px solid rgba(179,38,30,0.22)"
               : "1px solid transparent";
  return (
    <div style={{ background: bg, border, borderRadius: 8, padding: 10 }}>
      <div onClick={() => setOpen(o => !o)}
           style={{ display: "flex", alignItems: "baseline", marginBottom: open ? 6 : 0, cursor: "pointer", userSelect: "none", gap: 6 }}
           title={open ? "Свернуть" : "Развернуть детализацию"}>
        <span style={{ display: "inline-flex", alignSelf: "center", color: "var(--mute)" }}>
          <Icon name={open ? "chevron_d" : "chevron_r"} size={12}/>
        </span>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
        {region && <span style={{ fontSize: 11, color: "var(--mute)", marginLeft: 6 }}>· {region}</span>}
        <div style={{ marginLeft: "auto", fontWeight: 700, color: totalColor }}>{rub(total)}</div>
      </div>
      {open && children}
    </div>
  );
}


/** Строка налога с раскрываемой детализацией базы. */
function TaxLineRow({ line }: { line: any }) {
  const [open, setOpen] = useState(false);
  const details = line.tax_details;
  const breakdown: any[] = details?.breakdown || [];
  const hasBreakdown = breakdown.length > 0;
  return (
    <>
      <tr>
        <td className="tabular">{fmtDate(line.date)}</td>
        <td>{line.category_name}</td>
        <td>
          <div>{line.supplier || line.description || <span className="muted">—</span>}</div>
          {details && (
            <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 3 }}>
              База: <b>{rub(details.base_amount || 0)}</b>
              <span style={{ color: "var(--mute)" }}>{" "}× </span>
              <b>{Math.round((details.rate || 0) * 100)}%</b>
              {details.base_description && (
                <span> · {details.base_description}</span>
              )}
              {hasBreakdown && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={() => setOpen(v => !v)}
                    style={{
                      background: "none", border: "none", padding: 0,
                      color: "var(--red)", cursor: "pointer", font: "inherit",
                      fontSize: 11, textDecoration: "underline",
                    }}
                  >
                    {open ? "Скрыть детали" : `Показать ${breakdown.length} источник${breakdown.length === 1 ? "" : breakdown.length < 5 ? "а" : "ов"}`}
                  </button>
                </>
              )}
            </div>
          )}
        </td>
        <td className="num tabular" style={{ color: "var(--red)" }}>{rub(line.amount)}</td>
      </tr>
      {open && hasBreakdown && (
        <tr>
          <td colSpan={4} style={{ padding: 0 }}>
            <div style={{
              background: "var(--paper)", padding: "8px 12px",
              borderTop: "1px dashed var(--line-soft)",
              fontSize: 11, color: "var(--ink-3)",
            }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, fontWeight: 600 }}>
                Источники базы налога ({breakdown.length})
              </div>
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                {breakdown.map((b, idx) => (
                  <div key={b.id || idx} style={{
                    display: "flex", gap: 8, padding: "3px 0",
                    borderBottom: "1px dotted var(--line-soft)",
                  }}>
                    <span style={{ color: "var(--mute)", minWidth: 76, whiteSpace: "nowrap" }}>
                      {fmtDate(b.date)}
                    </span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={b.note || ""}>
                      {b.label || "—"}
                      {b.category && <span style={{ color: "var(--mute-2)" }}> · {b.category}</span>}
                    </span>
                    <span className="num tabular" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {rub(b.amount || 0)}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line-soft)",
                display: "flex", justifyContent: "space-between", fontWeight: 700,
              }}>
                <span>Итого база:</span>
                <span className="num tabular">{rub(details?.base_amount || 0)}</span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** Кнопка «Сохранить отчисления в фонды» — создаёт manual allocations СРАЗУ
 *  (вне фиксации payout). После save сбрасывает overrides, обновляет preview. */
function SaveManualFundsButton({ allocations, onSaved }: {
  allocations: { fund_id: number; fund_name: string; amount: number }[];
  onSaved: () => void;
}) {
  const mut = useMutation({
    mutationFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      for (const a of allocations) {
        await createFundAllocation({
          fund_id: a.fund_id,
          amount: a.amount,
          source_kind: "manual",
          allocated_at: today,
          comment: `Ручное перечисление из preview дивидендов от ${today}`,
        });
      }
    },
    onSuccess: () => onSaved(),
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const total = allocations.reduce((s, a) => s + a.amount, 0);
  const disabled = mut.isPending || total <= 0;
  return (
    <button
      type="button"
      className="btn btn-primary"
      disabled={disabled}
      onClick={() => {
        if (!confirm(`Сохранить отчисления в ${allocations.length} ${allocations.length === 1 ? "фонд" : "фондов"} на сумму ${formatRub(total, { unit: true })}?\n\nДеньги попадут в фонды сразу как ручные зачисления.`)) return;
        mut.mutate();
      }}
      style={{ whiteSpace: "nowrap" }}
    >
      {mut.isPending ? "Сохраняю…" : <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="save" size={13}/>Сохранить {formatRub(total, { unit: true })} в фонды</span>}
    </button>
  );
}


/** Список ручных перечислений (manual fund_allocations) — отображаются как
 *  обычные «расходные» строки ниже блока с inputами. */
function ManualFundAllocationsList({ fundIds }: { fundIds: number[] }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["manual-fund-allocs-by-funds", fundIds.sort().join(",")],
    queryFn: async () => {
      if (fundIds.length === 0) return [];
      // Грузим allocations всех фондов, фильтруем только manual
      const all = await Promise.all(fundIds.map(fid => fetchFundAllocations({ fund_id: fid })));
      return all.flat().filter(a => a.source_kind === "manual");
    },
  });
  const delMut = useMutation({
    mutationFn: (id: number) => deleteFundAllocation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["manual-fund-allocs-by-funds"] });
      void qc.invalidateQueries({ queryKey: ["dividend-preview-sheet"] });
      void qc.invalidateQueries({ queryKey: ["funds"] });
    },
  });
  if (q.isLoading) return null;
  const items: FundAllocationRow[] = q.data || [];
  if (items.length === 0) return null;
  // Сортируем по дате DESC
  items.sort((a, b) => (b.allocated_at || "").localeCompare(a.allocated_at || ""));
  const total = items.reduce((s, a) => s + a.amount, 0);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, marginBottom: 6 }}>
        Ручные перечисления в фонды (всего {items.length} на {formatRub(total, { unit: true })})
      </div>
      <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
        <thead>
          <tr>
            <th style={{ width: 100 }}>Дата</th>
            <th>Фонд</th>
            <th>Комментарий</th>
            <th className="num" style={{ width: 130 }}>Сумма</th>
            <th className="no-print" style={{ width: 40 }}/>
          </tr>
        </thead>
        <tbody>
          {items.map(a => (
            <tr key={a.id}>
              <td className="tabular">{a.allocated_at?.slice(0, 10) || "—"}</td>
              <td><strong>{a.fund_name || `Фонд #${a.fund_id}`}</strong></td>
              <td style={{ color: "var(--mute)" }}>{a.comment || "—"}</td>
              <td className="num tabular strong" style={{ color: "var(--red)" }}>
                {formatRub(a.amount)}
              </td>
              <td className="no-print">
                <button
                  className="btn btn-sm"
                  style={{ color: "var(--red)", padding: "2px 6px" }}
                  title="Удалить перечисление"
                  onClick={() => {
                    if (confirm(`Удалить перечисление ${formatRub(a.amount)} в «${a.fund_name}»?`)) {
                      delMut.mutate(a.id);
                    }
                  }}
                ><Icon name="trash" size={12}/></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


/* ─── Мини-модалка подтверждения (дата + заметка) ─────────────────── */
function ConfirmFixModal({ snap, owners, totalPct, periodFrom, periodTo, fundAllocations, npAfterFunds, onClose, onCreated }: {
  snap: PayoutSnapshot; owners: OwnerShare[]; totalPct: number;
  periodFrom: string; periodTo: string;
  fundAllocations: { fund_id: number; amount: number }[];
  npAfterFunds: number;
  onClose: () => void; onCreated: (id: number) => void;
}) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [paidAt, setPaidAt] = useState(todayIso);
  const [note, setNote] = useState("");
  // Фиксация включает ТОЛЬКО объекты, принятые к учёту — показываем ровно
  // те цифры, которые будут сохранены (preview с accepted_only).
  const gatedQ = useQuery({
    queryKey: ["dividend-preview-fix", periodFrom, periodTo],
    queryFn: () => previewDividendPayout({
      date_from: periodFrom || undefined,
      date_to: periodTo || undefined,
      accepted_only: true,
    }),
    staleTime: 10_000,
  });
  const gsnap = gatedQ.data;
  const payoutObjects = gsnap?.payout_objects ?? [];
  // Галочки: владелец может убрать объект из этой фиксации (его «К УЧЁТУ»
  // останется к следующей выплате).
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const toggleExcluded = (oid: number) => setExcludedIds(prev => {
    const n = new Set(prev);
    if (n.has(oid)) n.delete(oid); else n.add(oid);
    return n;
  });
  const includedObjects = payoutObjects.filter(o => !excludedIds.has(o.object_id));
  const sumAccepted = includedObjects.reduce((s, o) => s + (o.amount || 0), 0);
  const overheadTotal = gsnap?.totals.overhead ?? 0;
  const npGross = sumAccepted - overheadTotal;
  const totalFundAlloc = fundAllocations.reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const npAfterFundsGated = Math.max(0, npGross - totalFundAlloc);
  const excludedCount = Math.max(0, snap.revenues_by_object.length - payoutObjects.length);
  const qcFix = useQueryClient();
  const cancelAcceptMut = useMutation({
    mutationFn: (oid: number) => cancelObjectDistributionAcceptance(oid),
    onSuccess: () => {
      gatedQ.refetch();
      qcFix.invalidateQueries({ queryKey: ["dividend-preview-sheet"] });
    },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const mut = useMutation({
    mutationFn: () => createDividendPayout({
      paid_at: paidAt,
      period_from: periodFrom || null,
      period_to:   periodTo   || null,
      owners_count: owners.length || 2,
      owners: owners.length > 0 ? owners : null,
      note: note.trim() || null,
      fund_allocations: fundAllocations,  // override отчислений в фонды
      exclude_object_ids: Array.from(excludedIds),
    }),
    onSuccess: (det) => onCreated(det.id),
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const canSubmit = !!gsnap && includedObjects.length > 0 && npGross > 0
    && (owners.length === 0 || Math.abs(totalPct - 100) < 0.05);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Подтвердить фиксацию выплаты</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть"><Icon name="x"/></button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ padding: 10, background: "var(--paper-2)", borderRadius: 6, fontSize: 13 }}>
            <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", marginBottom: 6 }}>Что фиксируем (только принятое к учёту):</div>
            {gatedQ.isLoading ? (
              <div className="muted" style={{ padding: 6 }}>Считаем суммы к фиксации…</div>
            ) : !gsnap ? (
              <div className="alert alert-red"><div className="alert-body">Не удалось посчитать фиксацию: {(gatedQ.error as Error)?.message}</div></div>
            ) : payoutObjects.length === 0 ? (
              <div className="alert alert-amber"><div className="alert-body">
                Нет объектов со статусом «К УЧЁТУ». Примите маржу по объектам на расчётном листе
                (кнопка «Принять к учёту»), затем фиксируйте выплату.
              </div></div>
            ) : (
            <>
            <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 6, marginBottom: 8 }}>
              <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
                <thead><tr>
                  <th style={{ width: 30 }}/>
                  <th>Объект</th>
                  <th className="num" style={{ width: 110 }}>К учёту</th>
                  <th style={{ width: 36 }} title="Снять принятие к учёту"/>
                </tr></thead>
                <tbody>
                  {payoutObjects.map(o => {
                    const off = excludedIds.has(o.object_id);
                    return (
                      <tr key={o.object_id} style={off ? { opacity: 0.45 } : undefined}>
                        <td style={{ textAlign: "center" }}>
                          <input type="checkbox" checked={!off} onChange={() => toggleExcluded(o.object_id)}
                                 title={off ? "Вернуть в выплату" : "Убрать из этой выплаты (останется к следующей)"}/>
                        </td>
                        <td style={{ textDecoration: off ? "line-through" : undefined }}>{o.object_name}</td>
                        <td className="num tabular strong" style={{ color: "var(--green)" }}>{rub(o.amount)}</td>
                        <td style={{ textAlign: "center" }}>
                          <button className="icon-btn no-print" title="Снять принятие к учёту (вернёт в «не принято» на листе)"
                                  disabled={cancelAcceptMut.isPending}
                                  onClick={() => {
                                    if (confirm(`Снять принятие к учёту по «${o.object_name}» на ${rub(o.amount)}?`)) {
                                      cancelAcceptMut.mutate(o.object_id);
                                    }
                                  }}>
                            <Icon name="x" size={12}/>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 2, columnGap: 12 }}>
              <span>Период (накладные):</span>
              <span className="strong">{periodFrom || periodTo ? `${fmtDate(periodFrom)} — ${fmtDate(periodTo)}` : "всё время"}</span>
              <span>Объектов в выплате:</span>
              <span className="num tabular strong">{includedObjects.length}{excludedIds.size > 0 ? ` (убрано ${excludedIds.size})` : ""}</span>
              <span>Принятая маржа (Σ «К УЧЁТУ»):</span>
              <span className="num tabular" style={{ color: "var(--green)" }}>{rub(sumAccepted)}</span>
              <span>Накладные за период:</span>
              <span className="num tabular" style={{ color: "var(--red)" }}>−{rub(overheadTotal)}</span>
              {totalFundAlloc > 0 && (
                <>
                  <span>В фонды:</span>
                  <span className="num tabular" style={{ color: "var(--red)" }}>−{rub(totalFundAlloc)}</span>
                </>
              )}
              <span style={{ fontWeight: 700, borderTop: "1px solid var(--line)", paddingTop: 4 }}>ЧИСТАЯ ПРИБЫЛЬ:</span>
              <span className="num tabular strong" style={{ color: npAfterFundsGated >= 0 ? "var(--green)" : "var(--red)", borderTop: "1px solid var(--line)", paddingTop: 4 }}>
                {npAfterFundsGated >= 0 ? "" : "−"}{rub(Math.abs(npAfterFundsGated))}
              </span>
            </div>
            </>
            )}
            {excludedCount > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#8a6e10" }}>
                <Icon name="warn" size={11}/> Ещё {excludedCount} объектов с движением денег не приняты к учёту —
                в фиксацию не входят, их маржа останется к следующей выплате.
              </div>
            )}
            {owners.length > 0 && npAfterFundsGated > 0 && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line)" }}>
                {owners.map((o, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span>{o.name} ({(o.share_pct ?? 0).toFixed(1)}%)</span>
                    <span className="tabular strong">{rub(npAfterFundsGated * (Number(o.share_pct) || 0) / Math.max(totalPct, 1))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10 }}>
            <label>
              <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Дата выплаты</div>
              <input className="input" type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)}/>
            </label>
            <label>
              <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Заметка (необязательно)</div>
              <input className="input" value={note} onChange={e => setNote(e.target.value)}
                     placeholder="напр. «Майская выплата»"/>
            </label>
          </div>
          <div style={{ fontSize: 11, color: "var(--mute)" }}>
            После фиксации все суммы, объекты, лайн-айтемы и доли сохранятся неизменяемо.
            Изменить можно только дату выплаты и заметку.
          </div>
        </div>
        <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 18px 18px" }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={mut.isPending}>Отмена</button>
          <button className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending || !canSubmit}>
            {mut.isPending ? "Сохранение…" : "Зафиксировать"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Модалка top-up (доплата) ─────────────────────────────────── */
function TopUpModal({ parent, onClose, onCreated }: {
  parent: DividendPayoutDetail; onClose: () => void; onCreated: (id: number) => void;
}) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [paidAt, setPaidAt] = useState(todayIso);
  const [note, setNote] = useState(`Доплата к выплате от ${fmtDate(parent.paid_at)}`);
  const [owners, setOwners] = useState<OwnerShare[]>(parent.owners ?? []);
  const { user } = useAuth();
  const canEditShares = !!user?.is_superuser && user?.role === "admin";
  const cfgQ = useQuery({ queryKey: ["finance-payroll-configs"], queryFn: fetchPayrollConfigs, staleTime: 60_000 });
  const ownerCandidates = useMemo(
    () => (cfgQ.data?.items ?? []).filter(u => u.is_superuser || u.role === "owner"),
    [cfgQ.data]);

  const tot = parent.delta_after_payout.total;
  const totalPct = owners.reduce((s, o) => s + (Number(o.share_pct) || 0), 0);
  const mut = useMutation({
    mutationFn: () => createDividendTopUp(parent.id, {
      paid_at: paidAt, owners_count: owners.length || 2,
      owners: owners.length > 0 ? owners : null,
      note: note.trim() || null,
    }),
    onSuccess: (det) => onCreated(det.id),
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  const canSubmit = tot > 0 && (owners.length === 0 || Math.abs(totalPct - 100) < 0.05);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Зафиксировать доплату по выплате #{parent.id}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть"><Icon name="x"/></button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 13 }}>
            После выплаты от <b>{fmtDate(parent.paid_at)}</b> по тем же объектам поступило ещё:
          </div>
          <div style={{ padding: 10, background: "var(--paper-2)", borderRadius: 6, fontSize: 14, fontWeight: 700 }}>
            <span style={{ color: "var(--green)" }}>{rub(tot)}</span>
            <span style={{ fontSize: 11, color: "var(--mute)", marginLeft: 6, fontWeight: 400 }}>
              ({parent.delta_after_payout.count} операций)
            </span>
          </div>
          <label>
            <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Дата выплаты доплаты</div>
            <input className="input" type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)}/>
          </label>
          <OwnersEditor owners={owners} setOwners={setOwners} autoFromConfigs={ownerCandidates}
                        readonly={!canEditShares}/>
          <label>
            <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Заметка</div>
            <input className="input" value={note} onChange={e => setNote(e.target.value)}/>
          </label>
        </div>
        <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={mut.isPending}>Отмена</button>
          <button className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending || !canSubmit}>
            {mut.isPending ? "Сохранение…" : `Зафиксировать доплату ${rub(tot)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Модалка edit (paid_at + note) ────────────────────────────── */
function EditPayoutModal({ payout, onClose, onSaved }: {
  payout: DividendPayoutDetail; onClose: () => void; onSaved: () => void;
}) {
  const [paidAt, setPaidAt] = useState(payout.paid_at);
  const [note, setNote] = useState(payout.note ?? "");
  const mut = useMutation({
    mutationFn: () => patchDividendPayout(payout.id, { paid_at: paidAt, note: note.trim() || null }),
    onSuccess: () => onSaved(),
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Редактировать выплату #{payout.id}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть"><Icon name="x"/></button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 12, color: "var(--mute)" }}>
            Снимок неизменяем — поправить можно только дату выплаты и заметку.
          </div>
          <label>
            <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Дата выплаты</div>
            <input className="input" type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)}/>
          </label>
          <label>
            <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4 }}>Заметка</div>
            <input className="input" value={note} onChange={e => setNote(e.target.value)}/>
          </label>
        </div>
        <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={mut.isPending}>Отмена</button>
          <button className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Просмотр выплаты ─────────────────────────────────────────── */
function PayoutDetailModal({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const q = useQuery({
    queryKey: ["dividend-payout", id],
    queryFn: () => fetchDividendPayout(id),
    staleTime: 30_000,
  });
  const [editOpen, setEditOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-window dividend-payout-modal" style={{ maxWidth: 1000, maxHeight: "92vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header no-print">
          <h3>Выплата дивидендов #{id}</h3>
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            {q.data && (
              <>
                <button className="btn btn-sm" onClick={() => window.print()} title="Печать">
                  <Icon name="report" size={12}/> Печать
                </button>
                <button className="btn btn-sm" onClick={() => setEditOpen(true)}>
                  <Icon name="edit" size={12}/> Редактировать
                </button>
                {q.data.kind === "regular" && q.data.delta_after_payout.count > 0 && (
                  <button className="btn btn-sm btn-primary"
                          onClick={() => setTopUpOpen(true)}
                          title={`Зафиксировать ${rub(q.data!.delta_after_payout.total)} как доплату партнёрам`}>
                    <Icon name="plus" size={12}/> Зафиксировать доплату ({rub(q.data.delta_after_payout.total)})
                  </button>
                )}
              </>
            )}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть" style={{ marginLeft: 4 }}><Icon name="x"/></button>
        </div>
        <div className="modal-body dividend-payout-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {q.isLoading ? (
            <div className="muted" style={{ padding: 20 }}>Загрузка…</div>
          ) : q.error ? (
            <div className="alert alert-red"><div className="alert-body">{(q.error as Error).message}</div></div>
          ) : q.data && <PayoutDetailBody d={q.data} onStatusChanged={() => q.refetch()}/>}
        </div>
        {editOpen && q.data && <EditPayoutModal payout={q.data} onClose={() => setEditOpen(false)}
                                                onSaved={() => { setEditOpen(false); q.refetch(); onChanged(); }}/>}
        {topUpOpen && q.data && <TopUpModal parent={q.data} onClose={() => setTopUpOpen(false)}
                                            onCreated={() => { setTopUpOpen(false); q.refetch(); onChanged(); }}/>}
      </div>
    </div>
  );
}

function PayoutDetailBody({ d, onStatusChanged }: { d: DividendPayoutDetail; onStatusChanged?: () => void }) {
  const snap: PayoutSnapshot = d.snapshot;
  const { user } = useAuth();
  const canEditStatus = !!user?.is_superuser && user?.role === "admin";
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <Kpi label="Дата выплаты" value={fmtDate(d.paid_at)}/>
        <Kpi label="Тип" value={d.kind === "top_up" ? `Доплата к #${d.parent_payout_id}` : "Обычная выплата"}/>
        <Kpi label="Период" value={d.period_from || d.period_to ? `${fmtDate(d.period_from)} — ${fmtDate(d.period_to)}` : "всё время"}/>
        <Kpi label="Выручка" value={rub(d.total_revenue)} color="var(--green)"/>
        <Kpi label="Расходы по объектам" value={rub(d.total_expense_objects)} color="var(--red)"/>
        <Kpi label="Накладные" value={rub(d.total_overhead)} color="var(--red)"/>
        <Kpi label="ЧИСТАЯ ПРИБЫЛЬ" value={rub(d.net_profit)}
             color={d.net_profit >= 0 ? "var(--green)" : "var(--red)"} strong/>
        <Kpi label={`Партнёров: ${d.owners_count}`}
             value={d.owners ? "по долям" : `÷ ${d.owners_count} = ${rub(d.per_owner)}`}
             accent={!d.owners}/>
      </div>

      {d.fund_allocations && d.fund_allocations.length > 0 && (
        <div style={{ padding: 10, background: "var(--brick-50)", border: "1px solid var(--brick-100)", borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: "var(--brick-deep)", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>
            Отчислено в фонды — итого {rub(d.fund_allocations_total || 0)}
          </div>
          <table className="tbl tbl-tight" style={{ fontSize: 13, width: "100%" }}>
            <thead>
              <tr>
                <th>Фонд</th>
                <th>Комментарий</th>
                <th className="num" style={{ width: 110 }}>Дата</th>
                <th className="num" style={{ width: 130 }}>Сумма</th>
              </tr>
            </thead>
            <tbody>
              {d.fund_allocations.map(a => (
                <tr key={a.id}>
                  <td><strong>{a.fund_name || `Фонд #${a.fund_id}`}</strong></td>
                  <td style={{ color: "var(--mute)" }}>{a.comment || "—"}</td>
                  <td className="tabular">{fmtDate(a.allocated_at)}</td>
                  <td className="num tabular strong" style={{ color: "var(--red)" }}>
                    {rub(a.amount)}
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, borderTop: "2px solid var(--brick-100)" }}>
                <td colSpan={3}>Итого в фонды</td>
                <td className="num tabular" style={{ color: "var(--brick-deep)" }}>
                  {rub(d.fund_allocations_total || 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {d.owners && d.owners.length > 0 && (
        <div style={{ padding: 10, background: "rgba(46,125,50,0.06)", border: "1px solid rgba(46,125,50,0.22)", borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", marginBottom: 6 }}>
            Распределение между партнёрами
          </div>
          <table className="tbl tbl-tight" style={{ fontSize: 13, width: "100%" }}>
            <thead>
              <tr><th>Партнёр</th><th className="num">Доля</th><th className="num">Сумма</th></tr>
            </thead>
            <tbody>
              {d.owners.map((o, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Avatar name={o.name} userId={o.user_id || undefined} avatarFilename={o.avatar_filename || null} size={22} color="#4fae4e"/>
                      {o.name}
                    </div>
                  </td>
                  <td className="num tabular">{(Number(o.share_pct) || 0).toFixed(1)}%</td>
                  <td className="num tabular strong" style={{ color: "var(--green)" }}>{rub(o.amount ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d.note && <div style={{ fontSize: 13, color: "var(--mute)", display: "flex", alignItems: "center", gap: 6 }}><Icon name="edit" size={12}/>{d.note}</div>}

      <SnapshotSections snap={snap} canEditStatus={canEditStatus}
                        onStatusChanged={() => onStatusChanged?.()}/>

      {/* Доплаты после */}
      {d.kind === "regular" && (
        <div className="dividend-section">
          <SectionTitle>Доплачено после выплаты ({d.delta_after_payout.count}{d.delta_after_payout.top_ups_count > 0 ? `, уже распределено ${d.delta_after_payout.top_ups_count}` : ""})</SectionTitle>
          <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 6 }}>
            Приходы по объектам, которые вошли в эту выплату — но поступили <b>после</b> {fmtDate(d.paid_at)} и ещё не зафиксированы доплатой.
            Чтобы разделить между партнёрами — кнопка «Зафиксировать доплату» сверху.
          </div>
          {d.delta_after_payout.lines.length === 0 ? (
            <div className="muted" style={{ padding: 6 }}>Новых неучтённых поступлений нет.</div>
          ) : (
            <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
              <thead><tr>
                <th style={{ width: 90 }}>Дата</th><th>Объект</th><th>Контрагент</th>
                <th className="num" style={{ width: 110 }}>Сумма</th>
              </tr></thead>
              <tbody>
                {d.delta_after_payout.lines.map(l => (
                  <tr key={l.id}>
                    <td className="tabular">{fmtDate(l.received_at)}</td>
                    <td>{l.object_name}</td>
                    <td>{l.counterparty || <span className="muted">—</span>}</td>
                    <td className="num tabular" style={{ color: "var(--green)" }}>{rub(l.amount)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                  <td colSpan={3}>Итого к доплате</td>
                  <td className="num tabular" style={{ color: "var(--green)" }}>{rub(d.delta_after_payout.total)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}
